/**
 * ★ THE UPLOADER SWAP - the one place a build chooses which `QuarantineUploader` writes its files.
 *
 * IT FOLLOWS THE APP'S ONE LIVE FLAG, `isLiveApiConfigured()` (src/config.ts): with it set, the
 * real writer, ./firebase-quarantine-uploader.ts; with anything else, the fixture exactly as before
 * (`createFixtureQuarantineUploader`, ../application/api/fixture.ts). Before this file the
 * application flow handed out the fixture even in a live build, so every claim named an object that
 * had never been written and the API refused it.
 *
 * IT LIVES HERE, NOT IN THE APPLICATION FEATURE, because the editor's pictures are the second
 * writer into the same prefix and the editor cannot reach that feature's provider: the editor
 * builds its own from this switch (routes/articles/EditorRoute.tsx, `ArticleEditorScreen`). The
 * application flow re-exports this under the name it always used (../application/api/create.ts).
 *
 * THE STORAGE SDK IS LOADED LAZILY, AND ONLY HERE. The real writer is reached by a dynamic
 * `import()` at the FIRST WRITE, so the default build never runs it and never fetches its chunk -
 * the build the accessibility leg checks, because "THE SPA SEAM DEFAULTS TO FIXTURES"
 * (`scripts/a11y-roots.mjs`) - and a live build pays for the SDK only once somebody actually
 * chooses a file.
 */
import { isLiveApiConfigured } from '../../config';
import { createFixtureQuarantineUploader } from '../application/api/fixture';
import { TransportError } from '../application/api/problem';
import type { QuarantineUploader } from '../application/api/types';

/** How the real writer is fetched. A parameter so that a spec can hand in one that fails. */
export type QuarantineUploaderLoader = () => Promise<QuarantineUploader>;

/** The real writer's own chunk, which is where `firebase/storage` lives. */
const loadFirebaseQuarantineUploader: QuarantineUploaderLoader = async () =>
  (await import('./firebase-quarantine-uploader')).createFirebaseQuarantineUploader();

/**
 * A writer that loads its implementation at the first write and keeps it.
 *
 * A LOAD THAT FAILS IS NOT KEPT. A chunk that could not be fetched is a network failure and is
 * worded as one - a `TransportError`, "We could not reach MoonX" - and the next file tries the load
 * again rather than inheriting the failure until the page is reloaded. A refusal from the loaded
 * writer itself passes through untouched: it is already in the shape the screens word.
 */
export function createLazyQuarantineUploader(load: QuarantineUploaderLoader): QuarantineUploader {
  let loading: Promise<QuarantineUploader> | null = null;
  return {
    putQuarantineObject: async (intakeId, file, onProgress, call) => {
      loading ??= load();
      let uploader: QuarantineUploader;
      try {
        uploader = await loading;
      } catch (cause) {
        loading = null;
        throw new TransportError('The uploader could not be loaded.', cause);
      }
      return uploader.putQuarantineObject(intakeId, file, onProgress, call);
    },
  };
}

/** The flag is read at call time, so a spec can flip it; a build inlines it. */
export function createQuarantineUploader(): QuarantineUploader {
  if (isLiveApiConfigured()) {
    return createLazyQuarantineUploader(loadFirebaseQuarantineUploader);
  }
  return createFixtureQuarantineUploader();
}
